import type { ReactNode } from 'react';
import { css, styled } from 'styled-components';

import { H3 } from '#/components/Text/H3';
import { HeadingSmall } from '#/components/Text/HeadingSmall';
import { colors, cssBreakpoints } from '#/theme/theme';

const StyledTile = styled.div<{ $glowUp?: boolean }>`
  border-radius: 7px;
  box-shadow: 0 2px 4px rgb(0 0 0 / 0.1);
  background: ${colors.white};

  &[id] {
    scroll-margin-top: calc(83px + 36px);
  }

  margin-bottom: 36px;

  &:not(:first-child) {
    margin: 36px 0;
  }

  &[data-glow-up] {
    background:
      linear-gradient(${colors.white}, ${colors.white}) padding-box,
      linear-gradient(
          285deg,
          ${colors.brandYellow},
          ${colors.brandGreen},
          ${colors.brandBlue}
        )
        border-box;
    border: 2px solid transparent;
    border-radius: 4px;
  }

  &[data-disabled] {
    background-color: ${colors.backgroundGrey};
    opacity: 0.6;
    pointer-events: none;
  }
`;

const StyledTileBody = styled.div`
  padding: 20px;
  border-radius: 0 0 8px 8px;

  @media (min-width: ${cssBreakpoints.mdUp}) {
    padding: 40px;
  }

  & > *:last-child {
    margin-bottom: 0;
  }

  &[data-large] {
    padding: 36px;

    @media (min-width: ${cssBreakpoints.smUp}) {
      padding-top: 64px;
    }

    @media (min-width: ${cssBreakpoints.mdUp}) {
      padding: 96px;
    }
  }
`;

const DescriptionVariants = {
  large: css`
    margin-block-end: 56px;
  `,
  compact: css`
    margin-block-end: 1em;
  `,
};

const Description = styled.div<{
  $variant: keyof typeof DescriptionVariants | undefined;
}>`
  ${(props) => {
    if (!props.$variant) return;
    return DescriptionVariants[props.$variant];
  }}
`;

type Props = {
  id?: string;
  className?: string;
  preTitle?: string;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  large?: boolean;
  compactDescription?: boolean;
  qaId?: string;
  disabled?: boolean;
  beforeSection?: ReactNode;
  glowUp?: boolean;
};

export function FormSection({
  id,
  className,
  preTitle,
  title,
  description,
  children,
  large,
  compactDescription,
  qaId,
  disabled,
  beforeSection,
  glowUp,
}: Props) {
  let descriptionVariant: keyof typeof DescriptionVariants | undefined;
  if (large) descriptionVariant = 'large';
  if (compactDescription) descriptionVariant = 'compact';

  return (
    <>
      {beforeSection && (
        <div style={{ marginBottom: '24px' }}>{beforeSection}</div>
      )}
      <StyledTile
        id={id}
        className={className}
        data-qa-id={qaId}
        data-glow-up={glowUp ? true : undefined}
        data-disabled={disabled ? true : undefined}
      >
        <StyledTileBody data-large={large ? true : undefined}>
          {preTitle && (
            <div style={{ marginBottom: '-16px' }}>
              <HeadingSmall>{preTitle}</HeadingSmall>
            </div>
          )}

          {title && (
            <H3
              style={{ marginBottom: large && !description ? 56 : undefined }}
            >
              {title}
            </H3>
          )}

          {description && (
            <Description $variant={descriptionVariant}>
              {' '}
              {description}
            </Description>
          )}

          {children}
        </StyledTileBody>
      </StyledTile>
    </>
  );
}
